// modules/intro.js

const INTRO_KEY = 'introPlayedAt';
const INTRO_EXPIRE_MS = 10 * 60 * 1000; // 10 分鐘冷卻
const VIDEO_READY_TIMEOUT = 5000;
const RESUME_GUARD_DELAY = 1000;
const SPLIT_LEAD_TIME = 0.8;

// -------------------------------------------------
// Dev: ?reset=true 時清掉 sessionStorage
// -------------------------------------------------
(function handleResetParam() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('reset')) return;

  sessionStorage.removeItem(INTRO_KEY);
  console.log('[Dev] SessionStorage cleared!');

  const cleanUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  window.history.replaceState({ path: cleanUrl }, '', cleanUrl);
})();

// 輔助函式
function getLastPlayedAt() {
  const value = sessionStorage.getItem(INTRO_KEY);
  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function shouldPlayIntro() {
  const lastPlayedAt = getLastPlayedAt();
  if (!lastPlayedAt) return true;
  return Date.now() - lastPlayedAt > INTRO_EXPIRE_MS;
}

function markIntroPlayed() {
  sessionStorage.setItem(INTRO_KEY, String(Date.now()));
  console.log('[Intro] Playback marked in SessionStorage.');
}

/**
 * 檢查影片是否準備就緒
 */
function checkVideoReady(video, timeout = VIDEO_READY_TIMEOUT) {
  return new Promise((resolve) => {
    if (!video || video.error) {
      resolve('error');
      return;
    }
    if (video.readyState >= 2) {
      resolve('ready');
      return;
    }

    let settled = false;
    let timer = null;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      if (timer) clearTimeout(timer);
      resolve(result);
    };

    const onReady = () => finish('ready');
    const onError = () => finish('error');

    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('error', onError, { once: true });
    timer = setTimeout(() => finish('timeout'), timeout);
  });
}

// -------------------------------------------------
// 主功能模組
// -------------------------------------------------
export function initIntro() {
  const container = document.getElementById('intro-animation');
  const videoWrapper = document.getElementById('intro-video-wrapper');
  const video = document.getElementById('intro-single-video');

  // 基本檢查
  if (!container || !video || !window.gsap) return null;

  const gsap = window.gsap;
  const topPane = container.querySelector('.split-top');
  const bottomPane = container.querySelector('.split-bottom');

  let hasTriggered = false; // 是否已啟動開門動畫
  let hasStoredPlayed = false; // 是否已寫入 Storage
  let visibilityTimer = null;

  // -------------------------------------------------
  // 共用工具
  // -------------------------------------------------
  function setContainerVisible() {
    gsap.set(container, { visibility: 'visible', autoAlpha: 1 });
  }

  function releaseVideoResource() {
    video.pause();
    video.removeAttribute('src');
    video.load();
  }

  function getPreloadElement() {
    return $('#preload');
  }

  function fadeOutPreload(onComplete) {
    const $preload = getPreloadElement();

    if ($preload.length) {
      $preload.delay(500).fadeOut(500, onComplete);
      return true;
    }

    if (typeof onComplete === 'function') {
      onComplete();
    }
    return false;
  }

  function bindPlaybackListeners() {
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', runSplitAnimation, { once: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // -------------------------------------------------
  // 清理與狀態控制
  // -------------------------------------------------
  function cleanupListeners() {
    video.removeEventListener('timeupdate', handleTimeUpdate);
    video.removeEventListener('ended', runSplitAnimation);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  function removeIntro() {
    cleanupListeners();
    if (visibilityTimer) {
      clearTimeout(visibilityTimer);
      visibilityTimer = null;
    }

    try {
      releaseVideoResource(); // 釋放記憶體
    } catch (error) {
      console.warn('[Intro] Failed to release video resource.', error);
    }

    document.body.classList.remove('is-intro-active');
    if (container.parentNode) {
      container.remove();
    }
    if (window.ScrollTrigger) {
      window.ScrollTrigger.refresh();
    }
  }

  function resetIntroState() {
    hasTriggered = false;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', ''); // 依照要求保留
    video.setAttribute('preload', 'auto');

    gsap.killTweensOf([videoWrapper, topPane, bottomPane]);
    gsap.set(container, { autoAlpha: 1, visibility: 'hidden' });

    if (videoWrapper) gsap.set(videoWrapper, { autoAlpha: 1, clearProps: 'transform' });
    if (topPane) gsap.set(topPane, { yPercent: 0, clearProps: 'transform' });
    if (bottomPane) gsap.set(bottomPane, { yPercent: 0, clearProps: 'transform' });
  }

  function safeMarkPlayed() {
    if (hasStoredPlayed) return;
    hasStoredPlayed = true;
    markIntroPlayed();
  }

  // -------------------------------------------------
  // 動畫與事件處理
  // -------------------------------------------------
  function runSplitAnimation() {
    if (hasTriggered) return;
    hasTriggered = true;

    cleanupListeners();

    const tl = gsap.timeline({ onComplete: removeIntro });

    if (videoWrapper) {
      tl.to(videoWrapper, { autoAlpha: 0, duration: 0.5, ease: 'power1.out' }, 0);
    }
    if (topPane) {
      tl.to(topPane, { yPercent: -100, duration: 2.5, ease: 'power3.inOut' }, '<0.1');
    }
    if (bottomPane) {
      tl.to(bottomPane, { yPercent: 100, duration: 2.5, ease: 'power3.inOut' }, '<');
    }
  }

  function handleTimeUpdate() {
    if (!video.duration || hasTriggered) return;
    const timeLeft = video.duration - video.currentTime;
    if (timeLeft <= SPLIT_LEAD_TIME) {
      runSplitAnimation();
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState !== 'visible') return;
    if (!document.body.classList.contains('is-intro-active')) return;
    if (hasTriggered) return;

    if (visibilityTimer) clearTimeout(visibilityTimer);
    visibilityTimer = setTimeout(() => {
      if (!hasTriggered) {
        console.warn('[Intro] Force close due to resume freeze');
        runSplitAnimation();
      }
    }, RESUME_GUARD_DELAY);
  }

  // -------------------------------------------------
  // Fallback 與播放控制
  // -------------------------------------------------
  function handleFallback() {
    const fallbackEl = document.querySelector('.intro-play-fallback');

    if (fallbackEl) fallbackEl.classList.add('is-fall');
    gsap.set(videoWrapper, { autoAlpha: 0 });

    const playFallbackExit = () => {
      if (fallbackEl) {
        gsap.to(fallbackEl, {
          autoAlpha: 0,
          duration: 0.5,
          delay: 1,
          ease: 'power1.out',
          onComplete: runSplitAnimation
        });
        return;
      }

      setTimeout(runSplitAnimation, 1000);
    };

    fadeOutPreload(playFallbackExit);
    safeMarkPlayed(); // 也要記錄，避免再次出現動畫
  }

  async function playVideoAfterPreload() {
    await video.play();
    safeMarkPlayed();
    bindPlaybackListeners();
  }

  async function startMainPlayback() {
    try {
      await playVideoAfterPreload();
    } catch (error) {
      console.warn('[Intro] Final play failed after fade.', error);
      handleFallback();
    }
  }

  async function startPlayback() {
    resetIntroState();

    const result = await checkVideoReady(video, VIDEO_READY_TIMEOUT);

    if (result !== 'ready') {
      console.warn('[Intro] Video unavailable, triggering fallback.');
      setContainerVisible();
      handleFallback();
      return;
    }

    setContainerVisible();

    try {
      video.currentTime = 0;
    } catch (e) {}

    try {
      // 測試是否允許自動播放 (Autoplay Test)
      const playPromise = video.play();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Playback timeout')), 3000)
      );

      await Promise.race([playPromise, timeoutPromise]);

      // 測試成功：先暫停，等待 Preload 消失後再正式開始
      video.pause();
      try { video.currentTime = 0; } catch (e) {}

      const hasPreload = fadeOutPreload(() => {
        startMainPlayback();
      });

      if (!hasPreload) {
        await playVideoAfterPreload();
      }

    } catch (error) {
      // 測試失敗 (如：開啟低耗能模式)：進入 Fallback 流程
      console.warn('[Intro] Autoplay blocked or timeout.', error);
      // 安全機制：確保影片不會在背景繼續載入
      try {
        releaseVideoResource();
      } catch (e) {}
      handleFallback();
    }
  }

  // --- 初始化啟動判斷 ---
  container.style.visibility = 'hidden';

  if (!shouldPlayIntro()) {
    console.log('[Intro] Skipped (Time limit).');
    removeIntro();
    fadeOutPreload();
    return null;
  }

  document.body.classList.add('is-intro-active');
  resetIntroState();

  return function startAnimation() {
    startPlayback();
  };
}
